/**
 * Chart Performance Configuration
 *
 * Centralized settings for optimizing chart rendering performance.
 * Adjust these values to balance between performance and data fidelity.
 */

export interface TimelineConfig {
  /**
   * Maximum number of timeline points to process for charts.
   * Set to null, 0, or undefined to disable sampling (use all data points).
   *
   * Recommended values:
   * - 200-500: Good performance, slight visual quality loss
   * - 500-1000: Balanced performance and quality
   * - 1000+: High quality, may impact performance on slower devices
   * - null: No sampling, maximum quality, potential performance issues
   */
  maxTimelinePoints: number | null;

  /**
   * Strategy for sampling timeline data when maxTimelinePoints is exceeded.
   *
   * - 'uniform': Evenly spaced sampling across the entire timeline
   * - 'adaptive': More points at start/end, fewer in middle (preserves activity transitions)
   * - 'none': No sampling (same as setting maxTimelinePoints to null)
   */
  samplingStrategy: "uniform" | "adaptive" | "none";

  /**
   * Enable console logging for sampling operations.
   * Useful for debugging and performance monitoring.
   */
  enableSamplingLogs: boolean;
}

export interface ChartRenderingConfig {
  /**
   * Enable lazy loading for chart components.
   * Charts will only render when they come into view.
   */
  enableLazyLoading: boolean;

  /**
   * Enable memoization for chart components.
   * Prevents unnecessary re-renders when props haven't changed.
   */
  enableMemoization: boolean;

  /**
   * Delay (in ms) before rendering charts after activity switch.
   * Helps prevent UI blocking during rapid activity switching.
   */
  chartRenderDelay: number;

  /**
   * Enable progressive chart loading.
   * Load critical charts first, then load others progressively.
   */
  enableProgressiveLoading: boolean;
}

/**
 * Alternative configurations for different use cases
 */
export const TIMELINE_CONFIGS = {
  /** Maximum performance - minimal data points */
  PERFORMANCE: {
    maxTimelinePoints: 200,
    samplingStrategy: "uniform" as const,
    enableSamplingLogs: false
  },

  /** Balanced - good performance with decent quality */
  BALANCED: {
    maxTimelinePoints: 500,
    samplingStrategy: "uniform" as const,
    enableSamplingLogs: process.env.NODE_ENV === "development"
  },

  /** High quality - more data points, may impact performance */
  QUALITY: {
    maxTimelinePoints: 1000,
    samplingStrategy: "adaptive" as const,
    enableSamplingLogs: process.env.NODE_ENV === "development"
  },

  /** No sampling - maximum quality, potential performance issues */
  NO_SAMPLING: {
    maxTimelinePoints: null,
    samplingStrategy: "none" as const,
    enableSamplingLogs: false
  }
} as const;

/**
 * Utility function to easily switch between configurations
 *
 * Usage:
 * import { getTimelineConfig } from './chartPerformance';
 * const config = getTimelineConfig('PERFORMANCE');
 */
export const getTimelineConfig = (preset: keyof typeof TIMELINE_CONFIGS): TimelineConfig => {
  return TIMELINE_CONFIGS[preset];
};

/**
 * Default timeline configuration
 *
 * Using the BALANCED preset for optimal performance/quality trade-off.
 * For a 46-minute activity with ~2800 points, this reduces to ~500 points.
 *
 * To change presets, simply replace 'BALANCED' with:
 * - 'PERFORMANCE' for maximum speed (~200 points)
 * - 'QUALITY' for better visual quality (~1000 points)
 * - 'NO_SAMPLING' for maximum quality (all points)
 */
export const TIMELINE_CONFIG: TimelineConfig = getTimelineConfig("BALANCED");

/**
 * Default chart rendering configuration
 */
export const CHART_RENDERING_CONFIG: ChartRenderingConfig = {
  enableLazyLoading: true,
  enableMemoization: true,
  chartRenderDelay: 0, // Disabled for now to prevent loading issues
  enableProgressiveLoading: true
};

/**
 * Quick configuration switcher - uncomment the one you want to use:
 *
 * For maximum performance (fast activity switching, may reduce visual quality):
 * export const ACTIVE_TIMELINE_CONFIG = getTimelineConfig('PERFORMANCE');
 *
 * For balanced performance and quality (recommended):
 * export const ACTIVE_TIMELINE_CONFIG = getTimelineConfig('BALANCED');
 *
 * For high visual quality (slower activity switching):
 * export const ACTIVE_TIMELINE_CONFIG = getTimelineConfig('QUALITY');
 *
 * For maximum quality (no sampling, may cause performance issues):
 * export const ACTIVE_TIMELINE_CONFIG = getTimelineConfig('NO_SAMPLING');
 */

/**
 * Smart sampling configuration
 * Performance charts use BALANCED sampling for speed
 * Event charts use NO_SAMPLING to preserve critical ANT+ failure events
 */
export const PERFORMANCE_CHART_CONFIG = getTimelineConfig("BALANCED");
export const EVENT_CHART_CONFIG = getTimelineConfig("NO_SAMPLING");

/**
 * Adaptive sampling strategy
 * Uses smart sampling based on chart type and data characteristics
 */
export const ADAPTIVE_SAMPLING: TimelineConfig = {
  maxTimelinePoints: 1000, // Higher than BALANCED for better quality
  samplingStrategy: "adaptive", // Preserves start/end detail
  enableSamplingLogs: process.env.NODE_ENV === "development"
};

// Default configuration - using PERFORMANCE for fast activity switching
export const ACTIVE_TIMELINE_CONFIG = getTimelineConfig("PERFORMANCE");
